import React, {useState, useEffect} from 'react';
import {collection, getDocs, addDoc, deleteDoc, doc, Timestamp} from 'firebase/firestore';
import {firestoreDev} from '../../firebase';
import {FaClipboard, FaCheck, FaLink, FaClock, FaEye} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Add some CSS for the accordion
const accordionStyles = `
    .accordion-button:focus {
        box-shadow: none;
    }
    .accordion-arrow {
        transition: transform 0.2s ease;
    }
    .accordion-button[aria-expanded="true"] .accordion-arrow {
        transform: rotate(180deg);
    }
    .accordion-button {
        color: #495057 !important;
    }
    .accordion-button:hover {
        background-color: #f8f9fa;
    }
`;

const DemoLinksPage = () => {
    const [accessCodes, setAccessCodes] = useState([]);
    const [newAccessCode, setNewAccessCode] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [codeExplanation, setCodeExplanation] = useState('');
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [copiedCode, setCopiedCode] = useState(null);
    const [accessCodesSearch, setAccessCodesSearch] = useState('');
    const [loadingAccessCodes, setLoadingAccessCodes] = useState(true);
    
    useEffect(() => {
        const initializeData = async () => {
            try {
                if (!firestoreDev) {
                    throw new Error('Firebase is not initialized');
                }
                await loadAccessCodes(true);
            } catch (error) {
                console.error('Error initializing data:', error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        initializeData();
    }, []);

    const loadAccessCodes = async (firstLoad = false) => {
        if (!firestoreDev) return;
        setLoadingAccessCodes(true);
        try {
            const codesRef = collection(firestoreDev, 'demo-code');
            const snapshot = await getDocs(codesRef);
            const codes = snapshot.docs.map(doc => {
                const data = doc.data();
                // For Addy AI Team codes, create two separate entries
                if (data.explanation === 'Addy AI Team') {
                    return [
                        {
                            id: doc.id,
                            ...data,
                            url: `https://demo.addy.so/loans?code=${data.code}`
                        },
                        {
                            id: `${doc.id}-tour`,
                            ...data,
                            explanation: 'Addy AI Team but with tour',
                            url: `https://demo.addy.so/loans?tour=true&code=${data.code}`
                        }
                    ];
                }
                // For other codes, just create the normal URL
                return [{
                    id: doc.id,
                    ...data,
                    url: `https://demo.addy.so/loans?tourWithModal=true&code=${data.code}`
                }];
            }).flat(); // Flatten the array of arrays into a single array
            setAccessCodes(codes);
        } catch (error) {
            console.error('Error loading access codes:', error);
            setError('Failed to load access codes');
        } finally {
            setLoadingAccessCodes(false);
        }
    };

    const addAccessCode = async (uid) => {
        setLoadingAccessCodes(true);
        if (!firestoreDev) return;
        if (!uid || !expirationDate) return;
        if (!codeExplanation) return;
        // if code name is already taken, return
        const codeNameExists = accessCodes.some(code => code.code === uid);
        if (codeNameExists) {
            setError('Code name already exists');
            toast.error('Code name already exists');
            return;
        }
        try {
            const codesRef = collection(firestoreDev, 'demo-code');
            await addDoc(codesRef, {
                code: uid,
                expiration: Timestamp.fromDate(new Date(expirationDate)),
                createdAt: Timestamp.now(),
                explanation: codeExplanation
            });
            toast.success('Demo link added successfully');
        } catch (error) {
            console.error('Error adding access code:', error);
            setError('Failed to add access code');
            toast.error('Failed to add demo link');
        } finally {
            setExpirationDate('');
            setCodeExplanation('');
            loadAccessCodes(false);
            setLoadingAccessCodes(false);
        }
    };

    const removeAccessCode = async (id) => {
        setLoadingAccessCodes(true);
        if (!firestoreDev) return;
        try {
            const codeRef = doc(firestoreDev, 'demo-code', id);
            await deleteDoc(codeRef);
            loadAccessCodes(false);
            toast.success('Demo link removed successfully');
        } catch (error) {
            console.error('Error removing access code:', error);
            setError('Failed to remove access code');
            toast.error('Failed to remove demo link');
        } finally {
            setLoadingAccessCodes(false);
        }
    };

    const isCodeExpired = (expirationTimestamp) => {
        if (!expirationTimestamp) return true;
        const now = Timestamp.now();
        return now.seconds > expirationTimestamp.seconds;
    };

    const copyToClipboard = (text, codeId) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedCode(codeId);
            toast.info('URL copied to clipboard');
            setTimeout(() => setCopiedCode(null), 2000);
        });
    };

    const filteredAccessCodes = accessCodes.filter(code =>
        code.explanation.toLowerCase().includes(accessCodesSearch.toLowerCase()) ||
        code.url.toLowerCase().includes(accessCodesSearch.toLowerCase())
    );

    if (isLoading) {
        return (
            <div className="container-fluid p-4">
                <div className="d-flex justify-content-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid p-4">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-4 DemoLinksPageContainer">
            <style dangerouslySetInnerHTML={{ __html: accordionStyles }} />
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <h1>Demo Links Management</h1>

            {/* Instructions Section - Collapsible */}
            <div className="card mb-4">
                <div className="card-header">
                    <h2 className="h5 mb-0">
                        <button 
                            className="accordion-button collapsed p-2 text-decoration-none w-100 d-flex justify-content-between align-items-center border-0"
                            type="button" 
                            data-bs-toggle="collapse" 
                            data-bs-target="#instructionsCollapse" 
                            aria-expanded="false" 
                            aria-controls="instructionsCollapse"
                        >
                            <span>📖 How to Use Demo Links</span>
                            <span className="accordion-arrow">▼</span>
                        </button>
                    </h2>
                </div>
                <div className="collapse" id="instructionsCollapse">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-6">
                                <h6>✅ How to Create a Demo Link:</h6>
                                <ol>
                                    <li>Enter who the link is for (e.g., "Client Meeting", "Sales Demo")</li>
                                    <li>Choose when the link should expire</li>
                                    <li>Click "Add Link"</li>
                                    <li>Copy the generated link and share it</li>
                                </ol>
                                
                                <h6 className="mt-4">🔍 How to Manage Links:</h6>
                                <ul>
                                    <li>Use the search box to find specific links</li>
                                    <li>Click the copy button to copy a link</li>
                                    <li>Click "Remove" to delete old links</li>
                                </ul>
                            </div>
                            <div className="col-md-6">
                                <h6>📊 What the Status Means:</h6>
                                <ul className="list-unstyled">
                                    <li className="mb-2">
                                        <span className="badge bg-success me-2">Active</span>
                                        Link is working and can be used
                                    </li>
                                    <li className="mb-2">
                                        <span className="badge bg-danger me-2">Expired</span>
                                        Link has expired and won't work
                                    </li>
                                    <li className="mb-2">
                                        <span className="badge bg-warning me-2">Protected</span>
                                        Special links that cannot be deleted
                                    </li>
                                </ul>
                                
                                <h6 className="mt-4">🎯 Link Types:</h6>
                                <ul className="list-unstyled">
                                    <li className="mb-1"><strong>Standard Links:</strong> Include guided tour for new users</li>
                                    <li className="mb-1"><strong>Team Links:</strong> Clean version without tour</li>
                                    <li><strong>Team + Tour:</strong> Team version with optional tour</li>
                                </ul>
                            </div>
                        </div>
                        
                        <div className="alert alert-info mt-3" role="alert">
                            <strong>💡 Tips:</strong> Use descriptive names, set realistic expiration dates, and test links before sharing. Demo links are safe to use and only access the demo environment.
                        </div>
                    </div>
                </div>
            </div>

            {/* Demo Links Creation Section */}
            <div className="card mb-4">
                <div className="card-header">
                    <h2 className="h5 mb-0">
                        <FaLink className="me-2" />
                        Create New Demo Link
                    </h2>
                </div>
                <div className="card-body">
                    <div className="mb-3">
                        <div className="row g-4">
                            <div className="col-md-4">
                                <label htmlFor="codeExplanation" className="form-label">
                                    <strong>Who is this link for?</strong>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g., Client Demo, Investor Presentation"
                                    value={codeExplanation}
                                    onChange={(e) => setCodeExplanation(e.target.value)}
                                />
                                <div className="form-text">Be descriptive to easily find this link later</div>
                            </div>
                            <div className="col-md-4">
                                <label htmlFor="expirationDate" className="form-label">
                                    <strong>When should this link expire?</strong>
                                </label>
                                <input
                                    type="datetime-local"
                                    className="form-control"
                                    value={expirationDate}
                                    onChange={(e) => setExpirationDate(e.target.value)}
                                />
                                <div className="form-text">Recommended: 1-7 days from now</div>
                            </div>
                            <div className="col-md-2 d-flex align-items-end">
                                <button
                                    className="btn btn-primary w-100"
                                    onClick={() => {
                                        const uid = crypto.randomUUID();
                                        addAccessCode(uid);
                                    }}
                                    disabled={loadingAccessCodes || !codeExplanation || !expirationDate}
                                >
                                    {loadingAccessCodes ? 'Creating...' : 'Add Link'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Demo Links Management Section */}
            <div className="card">
                <div className="card-header">
                    <h2 className="h5 mb-0">
                        <FaEye className="me-2" />
                        Manage Demo Links
                    </h2>
                </div>
                <div className="card-body">
                    <div className="mb-3">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search demo links by purpose or URL..."
                            value={accessCodesSearch}
                            onChange={(e) => setAccessCodesSearch(e.target.value)}
                        />
                    </div>
                    <div className="table-responsive" style={{maxHeight: '400px', overflowY: 'auto'}}>
                        <table className="table">
                            <thead style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1}}>
                                <tr>
                                    <th>Purpose</th>
                                    <th><FaClock className="me-1" />Expiration</th>
                                    <th>Status</th>
                                    <th>Demo Link</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAccessCodes.map((code) => (
                                    <tr key={code.id}>
                                        <td>
                                            <strong>{code.explanation}</strong>
                                        </td>
                                        <td>{code.expiration?.toDate().toLocaleString()}</td>
                                        <td>
                                            <span className={`badge ${isCodeExpired(code.expiration) ? 'bg-danger' : 'bg-success'}`}>
                                                {isCodeExpired(code.expiration) ? 'Expired' : 'Active'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <a
                                                    href={code.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-truncate"
                                                    style={{maxWidth: '300px'}}
                                                >
                                                    {code.url}
                                                </a>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => copyToClipboard(code.url, code.id)}
                                                    title="Copy URL"
                                                >
                                                    {copiedCode === code.id ? (
                                                        <FaCheck />
                                                    ) : (
                                                        <FaClipboard />
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-danger btn-sm"
                                                onClick={() => removeAccessCode(code.id)}
                                                style={{ display: code.explanation.includes('Addy AI Team') ? 'none' : 'inline-block' }}
                                            >
                                                Remove
                                            </button>
                                            {code.explanation.includes('Addy AI Team') && (
                                                <span className="badge bg-warning">Protected</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {filteredAccessCodes.length === 0 && (
                        <div className="text-center py-4 text-muted">
                            <FaLink size={48} className="mb-3 opacity-50" />
                            <p>No demo links found. Create your first demo link above!</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DemoLinksPage; 